import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Lightbulb, Plus } from 'lucide-react';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { EnterprisePage } from '../components/layout/EnterprisePage';
import { DataGridEnterprise } from '../components/layout/DataGridEnterprise';
import type { DataGridColumn } from '../components/layout/DataGridEnterprise';
import { EnterpriseBadge } from '../components/layout/EnterpriseTable';
import { EmptyState } from '../components/EmptyState';
import { useAppToast } from '../components/AppToast';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { useOrgMembers } from '../hooks/useOrgMembers';
import { useOpportunities, useOpportunityOutcomeCounts } from '../hooks/useOpportunities';
import { formatOutcomeSummary, type OpportunityOutcomeRef } from '../services/opportunityService';
import type { OpportunityFilters } from '../services/opportunityService';
import { NewOpportunityModal } from '../components/oportunidades/NewOpportunityModal';
import { OpportunityDetailDrawer } from '../components/oportunidades/OpportunityDetailDrawer';
import {
  DERIVED_PRIORITY_BADGE,
  DERIVED_PRIORITY_LABELS,
  OPPORTUNITY_LEVELS,
  OPPORTUNITY_LEVEL_BADGE,
  OPPORTUNITY_LEVEL_LABELS,
  OPPORTUNITY_SOURCE_KINDS,
  OPPORTUNITY_SOURCE_LABELS,
  OPPORTUNITY_STATUSES,
  OPPORTUNITY_STATUS_BADGE,
  OPPORTUNITY_STATUS_LABELS,
  categoryLabel,
  deriveOpportunityPriority,
  type Opportunity,
  type OpportunityLevel,
  type OpportunitySourceKind,
  type OpportunityStatus,
} from '../types/opportunity';
import type { StatusCapsuleItem } from '../components/layout/StatusCapsule';

const filterClass =
  'h-8 min-w-[140px] flex-1 rounded border border-[var(--border-default)] bg-white px-2 text-[11px] text-slate-700 outline-none focus:border-blue-500 dark:bg-slate-900 dark:text-slate-200 sm:flex-none';

function useDebounced(value: string, delay = 300): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export const OportunidadesPage: React.FC = () => {
  const organizationId = useOrganizationId();
  const { user } = useAuth();
  const { members } = useOrgMembers(organizationId);
  const { showToast, ToastContainer } = useAppToast();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<OpportunityStatus | ''>('');
  const [impact, setImpact] = useState<OpportunityLevel | ''>('');
  const [effort, setEffort] = useState<OpportunityLevel | ''>('');
  const [sourceKind, setSourceKind] = useState<OpportunitySourceKind | ''>('');
  const [ownerId, setOwnerId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('id'));

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) setSelectedId(id);
  }, [searchParams]);
  const debouncedSearch = useDebounced(search);

  const filters = useMemo<OpportunityFilters>(() => ({
    ...(status ? { status } : {}),
    ...(impact ? { impact_level: impact } : {}),
    ...(effort ? { effort_level: effort } : {}),
    ...(sourceKind ? { source_kind: sourceKind } : {}),
    ...(ownerId ? { owner_id: ownerId } : {}),
    ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
  }), [status, impact, effort, sourceKind, ownerId, debouncedSearch]);

  const summaryQuery = useOpportunities(organizationId, {});
  const listQuery = useOpportunities(organizationId, filters);
  const summary = summaryQuery.data ?? [];
  const rows = listQuery.data ?? [];
  const hasFilters = Object.keys(filters).length > 0;
  const outcomeIds = useMemo(() => {
    const ids = new Set<string>();
    summary.forEach((item) => ids.add(item.id));
    rows.forEach((item) => ids.add(item.id));
    return [...ids].sort();
  }, [summary, rows]);
  const countsQuery = useOpportunityOutcomeCounts(organizationId, outcomeIds);
  const countsByOpportunity = useMemo(() => {
    const grouped = new Map<string, OpportunityOutcomeRef[]>();
    (countsQuery.data ?? []).forEach((item) => {
      const current = grouped.get(item.opportunity_id) ?? [];
      current.push(item);
      grouped.set(item.opportunity_id, current);
    });
    return grouped;
  }, [countsQuery.data]);

  const counts = useMemo(() => ({
    captured: summary.filter((item) => item.status === 'captured').length,
    under_analysis: summary.filter((item) => item.status === 'under_analysis').length,
    approved: summary.filter((item) => item.status === 'approved').length,
  }), [summary]);

  const toggleStatus = (next: OpportunityStatus) => {
    setStatus((current) => (current === next ? '' : next));
  };

  const unfoldedCount = useMemo(() => {
    const known = new Set(summary.map((item) => item.id));
    return new Set(
      (countsQuery.data ?? [])
        .map((item) => item.opportunity_id)
        .filter((id) => known.has(id)),
    ).size;
  }, [summary, countsQuery.data]);
  const summaryReady = summaryQuery.isSuccess;
  const outcomesReady = summary.length === 0 ? summaryReady : countsQuery.isSuccess;
  const statusItems: StatusCapsuleItem[] = [
    { label: 'Capturadas', value: summaryReady ? counts.captured : '—', color: 'slate', active: status === 'captured', onClick: () => toggleStatus('captured') },
    { label: 'Em análise', value: summaryReady ? counts.under_analysis : '—', color: 'sky', active: status === 'under_analysis', onClick: () => toggleStatus('under_analysis') },
    { label: 'Aprovadas', value: summaryReady ? counts.approved : '—', color: 'emerald', active: status === 'approved', onClick: () => toggleStatus('approved') },
    { label: 'Com desdobramento', value: summaryReady && outcomesReady ? unfoldedCount : '—', color: 'indigo' },
  ];

  const columns = useMemo<DataGridColumn<Opportunity>[]>(() => [
    {
      key: 'title',
      label: 'Oportunidade',
      width: '24%',
      render: (row) => (
        <span className="line-clamp-2 font-medium text-slate-800 dark:text-slate-100">{row.title}</span>
      ),
    },
    {
      key: 'source_kind',
      label: 'Origem',
      width: '11%',
      render: (row) => (
        <span className="block">
          <span className="block">{OPPORTUNITY_SOURCE_LABELS[row.source_kind]}</span>
          {row.source_label ? (
            <span className="mt-0.5 block truncate text-[11px] text-slate-500">{row.source_label}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'category',
      label: 'Categoria',
      width: '12%',
      render: (row) => categoryLabel(row.category),
    },
    {
      key: 'impact_level',
      label: 'Impacto',
      width: '10%',
      render: (row) => row.impact_level
        ? <EnterpriseBadge label={OPPORTUNITY_LEVEL_LABELS[row.impact_level]} color={OPPORTUNITY_LEVEL_BADGE[row.impact_level]} />
        : <span className="text-slate-400">—</span>,
    },
    {
      key: 'effort_level',
      label: 'Esforço',
      width: '10%',
      render: (row) => row.effort_level
        ? <EnterpriseBadge label={OPPORTUNITY_LEVEL_LABELS[row.effort_level]} color={OPPORTUNITY_LEVEL_BADGE[row.effort_level]} />
        : <span className="text-slate-400">—</span>,
    },
    {
      key: 'priority',
      label: 'Prioridade',
      width: '14%',
      render: (row) => {
        const priority = deriveOpportunityPriority(row.impact_level, row.effort_level);
        return <EnterpriseBadge label={DERIVED_PRIORITY_LABELS[priority]} color={DERIVED_PRIORITY_BADGE[priority]} />;
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: '11%',
      render: (row) => (
        <EnterpriseBadge label={OPPORTUNITY_STATUS_LABELS[row.status]} color={OPPORTUNITY_STATUS_BADGE[row.status]} />
      ),
    },
    {
      key: 'owner',
      label: 'Responsável',
      width: '10%',
      render: (row) => row.owner_name || '—',
    },
    {
      key: 'outcomes',
      label: 'Desdobramentos',
      width: '14%',
      render: (row) => formatOutcomeSummary(countsByOpportunity.get(row.id) ?? []) || '—',
    },
  ], [countsByOpportunity]);

  const openCreate = () => setCreateOpen(true);

  if (!organizationId) {
    return (
      <ModuleLayout>
        <EnterprisePage
          title="Oportunidades de Melhoria"
          subtitle="Centralize, priorize e transforme oportunidades em melhorias executáveis."
          icon={<Lightbulb size={16} />}
        >
          <EmptyState
            title="Nenhuma organização ativa"
            description="Entre em uma organização para capturar e priorizar oportunidades."
          />
        </EnterprisePage>
        <ToastContainer />
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout>
      <EnterprisePage
        title="Oportunidades de Melhoria"
        subtitle="Centralize, priorize e transforme oportunidades em melhorias executáveis."
        icon={<Lightbulb size={16} />}
        statusItems={statusItems}
        headerActions={(
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
          >
            <Plus size={14} />
            Nova oportunidade
          </button>
        )}
      >
        <div className="flex min-h-full flex-col gap-3 pb-4">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por título ou descrição"
              className={`${filterClass} min-w-[200px] flex-[2]`}
            />
            <select value={status} onChange={(event) => setStatus(event.target.value as OpportunityStatus | '')} className={filterClass}>
              <option value="">Status</option>
              {OPPORTUNITY_STATUSES.map((item) => (
                <option key={item} value={item}>{OPPORTUNITY_STATUS_LABELS[item]}</option>
              ))}
            </select>
            <select value={impact} onChange={(event) => setImpact(event.target.value as OpportunityLevel | '')} className={filterClass}>
              <option value="">Impacto</option>
              {OPPORTUNITY_LEVELS.map((item) => (
                <option key={item} value={item}>{OPPORTUNITY_LEVEL_LABELS[item]}</option>
              ))}
            </select>
            <select value={effort} onChange={(event) => setEffort(event.target.value as OpportunityLevel | '')} className={filterClass}>
              <option value="">Esforço</option>
              {OPPORTUNITY_LEVELS.map((item) => (
                <option key={item} value={item}>{OPPORTUNITY_LEVEL_LABELS[item]}</option>
              ))}
            </select>
            <select value={sourceKind} onChange={(event) => setSourceKind(event.target.value as OpportunitySourceKind | '')} className={filterClass}>
              <option value="">Origem</option>
              {OPPORTUNITY_SOURCE_KINDS.map((item) => (
                <option key={item} value={item}>{OPPORTUNITY_SOURCE_LABELS[item]}</option>
              ))}
            </select>
            <select value={ownerId} onChange={(event) => setOwnerId(event.target.value)} className={filterClass}>
              <option value="">Responsável</option>
              {members.map((member) => (
                <option key={member.id} value={member.id}>{member.name}</option>
              ))}
            </select>
          </div>

          {listQuery.isError ? (
            <div className="enterprise-card-surface flex min-h-[220px] flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm text-slate-600 dark:text-slate-300">Não foi possível carregar as oportunidades.</p>
              <button
                type="button"
                onClick={() => listQuery.refetch()}
                className="rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200"
              >
                Tentar de novo
              </button>
            </div>
          ) : (
            <DataGridEnterprise
              columns={columns}
              data={rows}
              rowKey={(row) => row.id}
              isLoading={listQuery.isLoading}
              selectedKey={selectedId}
              onRowClick={(row) => setSelectedId(row.id)}
              emptyIcon={<Lightbulb size={36} strokeWidth={1} />}
              emptyTitle={hasFilters ? 'Nenhuma oportunidade encontrada' : 'Nenhuma oportunidade capturada'}
              emptySubtitle={hasFilters
                ? 'Ajuste a busca ou os filtros para ver outros registros.'
                : 'Capture a primeira oportunidade para começar a priorizar.'}
              emptyAction={hasFilters ? undefined : (
                <button
                  type="button"
                  onClick={openCreate}
                  className="inline-flex items-center gap-1.5 rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                >
                  <Plus size={14} />
                  Nova oportunidade
                </button>
              )}
            />
          )}
        </div>
      </EnterprisePage>

      <NewOpportunityModal
        open={createOpen}
        organizationId={organizationId}
        createdBy={user?.id}
        members={members}
        onClose={() => setCreateOpen(false)}
        onCreated={() => showToast('success', 'Oportunidade capturada')}
        onError={(message) => showToast('error', message)}
      />

      <OpportunityDetailDrawer
        opportunityId={selectedId}
        organizationId={organizationId}
        members={members}
        onClose={() => setSelectedId(null)}
        onSaved={() => showToast('success', 'Oportunidade atualizada')}
        onDeleted={() => {
          setSelectedId(null);
          showToast('success', 'Oportunidade removida');
        }}
        onError={(message) => showToast('error', message)}
        onActionLinked={() => showToast('success', 'Ação criada e vinculada à oportunidade.')}
        onProjectLinked={() => showToast('success', 'Projeto criado e vinculado à oportunidade.')}
        onKaizenLinked={() => showToast('success', 'Kaizen criado e vinculado à oportunidade.')}
      />

      <ToastContainer />
    </ModuleLayout>
  );
};

export default OportunidadesPage;
